import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { makeRequest } from "../../../utils/axios.js";

export const useSearch = () => {
    const [query, setQuery] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [searchHistory, setSearchHistory] = useState([]);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const navigate = useNavigate();
    const searchRef = useRef(null);

    const handleOpenSearch = () => {
        const history = JSON.parse(localStorage.getItem("searchHistory")) || [];
        setSearchHistory(history);
        setIsSearchOpen(true);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setIsSearchOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [searchRef]);

    useEffect(() => {
        const fetchData = async () => {
            if (!query.trim()) {
                setSearchResults([]);
                return;
            }
            try {
                const res = await makeRequest.get(`/users/search?name=${query}`);
                setSearchResults(res.data);
            } catch (err) {
                console.log(err);
            }
        };

        const timeoutId = setTimeout(() => {
            if (query) fetchData();
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [query]);

    const handleSearchSubmit = (e) => {
        if (e.key === "Enter" && query.trim() !== "") {
            e.preventDefault();
            
            setSearchHistory(prev => {
                const keywordId = `kw_${query.trim().toLowerCase()}`;
                const keywordData = { id: keywordId, type: 'keyword', text: query.trim() };
                
                const filtered = prev.filter(item => item.id !== keywordId);
                const newHistory = [keywordData, ...filtered].slice(0, 5);
                localStorage.setItem("searchHistory", JSON.stringify(newHistory));
                return newHistory;
            });

            setIsSearchOpen(false);
            navigate(`/search?q=${encodeURIComponent(query.trim())}`);
        }
    };

    const handleSelectUser = (user) => {
        setSearchHistory(prev => {
            const userData = { ...user, type: 'user' }; 
            const filtered = prev.filter(item => item.id !== user.id);
            const newHistory = [userData, ...filtered].slice(0, 5);
            localStorage.setItem("searchHistory", JSON.stringify(newHistory));
            return newHistory;
        });

        setIsSearchOpen(false);
        setQuery("");
        navigate(`/profile/${user.id}`);
    };

    const handleHistoryClick = (item) => {
        setIsSearchOpen(false);
        setQuery("");
        if (item.type === 'keyword') {
            navigate(`/search?q=${encodeURIComponent(item.text)}`);
        } else {
            navigate(`/profile/${item.id}`);
        }
    };

    const clearHistory = () => {
        setSearchHistory([]);
        localStorage.removeItem("searchHistory");
    };

    const removeHistoryItem = (id) => {
        setSearchHistory(prev => {
            const newHistory = prev.filter(item => item.id !== id);
            localStorage.setItem("searchHistory", JSON.stringify(newHistory));
            return newHistory;
        });
    };

    return {
        query,
        setQuery,
        searchResults,
        searchHistory,
        isSearchOpen,
        searchRef,
        handleOpenSearch,
        handleSearchSubmit,
        handleSelectUser,
        handleHistoryClick,
        clearHistory,
        removeHistoryItem
    };
};